import React, { useMemo } from 'react';
import { ActivityIndicator, Pressable, View } from 'react-native';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { NavigationContext } from '@react-navigation/native';
import { Text } from '@constants/Themed';
import { XStack, YStack } from 'tamagui';
import apis from '@api/api';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import type { FieldContext } from '@types/handleFieldType';

function parseList(value: unknown): string[] {
  if (!value) return [];
  const parts = Array.isArray(value) ? value : String(value).split(',');
  return parts
    .flatMap((entry) => String(entry).split(','))
    .map((entry) => entry.trim())
    .filter(Boolean);
}

function resolveItemId(ctx: FieldContext): string | null {
  const candidates = [
    ctx.otherVals?.itemId,
    ctx.otherVals?.contentId,
    ctx.otherVals?._id,
    ctx.formValues?._id,
    ctx.formValues?.id,
  ];
  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === 'object') {
      const nestedId = candidate.$oid || candidate._id || candidate.id;
      if (nestedId) return String(nestedId);
      continue;
    }
    return String(candidate);
  }
  return null;
}

const KeywordLookupField = ({ ctx }: { ctx: FieldContext }) => {
  const { t } = useTranslation();
  const navigation = React.useContext(NavigationContext) as any;
  const theme = ctx.theme;
  const validation = ctx.schemaItem?.validation || ctx.schemaItem?.content?.validation || {};
  const keywords = useMemo(
    () => parseList(validation.keywords || validation.enum),
    [validation.keywords, validation.enum]
  );
  const itemId = resolveItemId(ctx);

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'keywordLookup',
      itemId,
      validation.dataSetId,
      validation.dataTags,
      keywords,
      validation.includeParticipation,
      validation.inferOpposite,
    ],
    queryFn: () => apis.keywordLookup({
      itemId: itemId as string,
      dataSetId: validation.dataSetId || undefined,
      dataTags: validation.dataTags || undefined,
      keywords,
      includeParticipation: validation.includeParticipation !== false,
      inferOpposite: validation.inferOpposite !== false,
    }),
    enabled: !!itemId && keywords.length > 0,
    staleTime: 60 * 1000,
  });

  const textPrimary = theme ? '#0f172a' : '#f8fafc';
  const textMuted = theme ? '#64748b' : '#94a3b8';
  const cardBg = theme ? '#f8fafc' : '#1e293b';
  const border = theme ? '#e2e8f0' : '#334155';

  if (!keywords.length) {
    return (
      <Text style={{ color: textMuted, fontSize: responsiveFontSize(13) }}>
        {t('keywordLookupNoKeywords')}
      </Text>
    );
  }

  if (!itemId) {
    return (
      <Text style={{ color: textMuted, fontSize: responsiveFontSize(13) }}>
        {t('keywordLookupSaveToSee')}
      </Text>
    );
  }

  if (isLoading) {
    return <ActivityIndicator />;
  }

  if (error || !data?.success) {
    return (
      <Text style={{ color: '#ef4444', fontSize: responsiveFontSize(13) }}>
        {t('keywordLookupFailed')}
      </Text>
    );
  }

  const winKey = keywords.find((keyword) => /win/i.test(keyword));
  const winRate = winKey && data.rates?.[winKey] != null
    ? Math.round(Number(data.rates[winKey]) * 100)
    : null;

  return (
    <YStack
      gap={10}
      style={{
        backgroundColor: cardBg,
        borderWidth: 1,
        borderColor: border,
        borderRadius: 12,
        padding: 12,
      }}
    >
      <XStack flexWrap="wrap" gap={8}>
        {keywords.map((keyword) => (
          <View
            key={keyword}
            style={{
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 999,
              backgroundColor: theme ? '#e0f2fe' : '#1e3a5f',
            }}
          >
            <Text style={{ color: textPrimary, fontSize: responsiveFontSize(12), fontWeight: '600' }}>
              {keyword}: {data.keywords?.[keyword]?.count ?? 0}
            </Text>
          </View>
        ))}
        <View
          style={{
            paddingHorizontal: 10,
            paddingVertical: 6,
            borderRadius: 999,
            backgroundColor: theme ? '#dcfce7' : '#14532d',
          }}
        >
          <Text style={{ color: textPrimary, fontSize: responsiveFontSize(12), fontWeight: '600' }}>
            {t('keywordLookupParticipated')}: {data.participated?.count ?? 0}
          </Text>
        </View>
        {winRate != null && (
          <View
            style={{
              paddingHorizontal: 10,
              paddingVertical: 6,
              borderRadius: 999,
              backgroundColor: theme ? '#fef3c7' : '#78350f',
            }}
          >
            <Text style={{ color: textPrimary, fontSize: responsiveFontSize(12), fontWeight: '700' }}>
              {t('keywordLookupWinRate')}: {winRate}%
            </Text>
          </View>
        )}
      </XStack>

      {(data.participated?.items || []).slice(0, 8).map((item: any) => (
        <Pressable
          key={String(item._id)}
          onPress={() => {
            if (item._id && item.dataId && navigation?.navigate) {
              navigation.navigate('GenericDataItemScreen', {
                dataId: String(item.dataId),
                contentId: String(item._id),
              });
            }
          }}
          style={{ paddingVertical: 4 }}
        >
          <Text style={{ color: textPrimary, fontSize: responsiveFontSize(13) }} numberOfLines={1}>
            {item.label || item.folderTitle || String(item._id).slice(-6)}
          </Text>
          {!!item.folderTitle && (
            <Text style={{ color: textMuted, fontSize: responsiveFontSize(11) }}>
              {item.folderTitle}
            </Text>
          )}
        </Pressable>
      ))}
    </YStack>
  );
};

export default KeywordLookupField;
